import { useEffect } from 'react'

/**
 * Hook to handle escape key press for closing modals/popups.
 *
 * Escape-layer contract (apps/web/AGENTS.md §Escape-layer): `preventDefault()`
 * is the cross-handler "this Escape is taken" signal — `useModulePaneEscapeClose`
 * and ChatBox's dock handler both decline already-prevented events. This hook
 * honors the same signal in BOTH directions: it skips events some higher-priority
 * consumer (e.g. a React `onKeyDown` on a focused inner layer, or ChatBox's dock
 * handler — React handlers run before window listeners) already claimed, and it
 * claims the events it does handle. That keeps one keypress to one layer.
 *
 * @param onEscape - Callback function to execute when Escape is pressed
 * @param enabled - Whether the hook is active (default: true)
 */
export function useEscapeKey(onEscape: () => void, enabled: boolean = true): void {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault()
        onEscape()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onEscape, enabled])
}
